import type { LocationCoordinates, LocationItem } from 'lib/queries/locations';

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Children,
  isValidElement,
} from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { green, lilac } from '@leafwell/components/tailwind/colors.cjs';

export type Coordinates = LocationCoordinates &
  Partial<Pick<LocationItem, 'id'>>;

export type LocationsMapProps = {
  center?: Coordinates;
  className?: string;
  locations?: Coordinates[];
  variant?: 'primary' | 'secondary';
  isActionable?: boolean;
  onSelect?: (id: LocationItem['id']) => void;
  zoom?: number;
  activeId?: number | string;
};

export type MapProps = {
  children: React.ReactNode;
  className?: string;
  mapOptions?: Omit<google.maps.MapOptions, 'center'> & {
    center?: Coordinates;
  };
  locations?: Coordinates[];
};

export type MarkersProps = {
  map?: google.maps.Map;
  markers?: google.maps.Marker[];
  activeId?: number | string;
  pinColors?: {
    fillColor?: string;
    borderColor?: string;
  };
  onSelect: (id: number) => void;
};

const resetMapBounds = markersCount =>
  markersCount > 1 ? new google.maps.LatLngBounds() : null;

const Map: React.FC<MapProps> = ({
  className,
  mapOptions,
  locations,
  children,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map & Partial<unknown>>();
  const boundsRef = useRef<google.maps.LatLngBounds>(null);

  const markers = useMemo(() => {
    // reset bound on locations change
    boundsRef.current = resetMapBounds(locations.length);

    return locations.map(({ id, ...coordinates }) => {
      const marker = new google.maps.Marker({
        position: coordinates,
      });

      marker.set('id', id);

      if (boundsRef?.current) {
        boundsRef.current.extend(marker.getPosition());
      }

      return marker;
    });
  }, [locations]);

  useEffect(() => {
    if (mapRef?.current && !map) {
      setMap(new window.google.maps.Map(mapRef.current, {}));
    }
  }, [mapRef, map]);

  useEffect(() => {
    if (map) {
      map.setOptions(mapOptions);
    }
  }, [map, mapOptions]);

  useEffect(() => {
    if (map && boundsRef?.current) {
      map.fitBounds(boundsRef.current);
    }
  }, [map, boundsRef]);

  return (
    <>
      <div ref={mapRef} className={['flex w-full', className].join(' ')} />
      {Children.map(children, child => {
        if (isValidElement(child)) {
          // set the map prop on the child component
          // @ts-ignore
          return React.cloneElement(child, { map, markers });
        }

        return null;
      })}
    </>
  );
};

const Markers: React.FC<MarkersProps> = ({
  map,
  markers,
  activeId,
  pinColors,
  onSelect = () => null,
}) => {
  const pinColorsSelected = {
    fillColor: lilac[600],
    borderColor: lilac[600],
  };
  const encodeSvg = useCallback(
    (svg: string) =>
      `data:image/svg+xml;charset=utf-8, ${encodeURIComponent(svg)}`,
    [],
  );

  const svgCluster: string = useMemo(() => {
    const svg = `<svg height="50" width="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg"><circle cx="25" cy="25" r="21" stroke="${pinColors.borderColor}" stroke-width="2" fill="${pinColors.fillColor}" /></svg>`;

    return encodeSvg(svg);
  }, [encodeSvg, pinColors]);

  const svgMarker: string = useMemo(() => {
    const svg =
      `<svg width="22" height="28" viewBox="0 0 22 28" fill="none" xmlns="http://www.w3.org/2000/svg">` +
      `<path d="M5.00016 20.5L10.0002 25.5C11.8334 24.3333 15.5 21.9 15.5 21.5C15.5 21.1 18.5 17.3333 20 15.5C20.3333 14.1667 21 11.4 21 11C21 10.6 20.3333 8.16667 20 7L16.5 2.5L11 0.5L5.00016 2.5L1.50016 7C1.33349 8.83333 1.10016 12.7 1.50016 13.5L5.00016 20.5Z" fill="${pinColors.fillColor}"/>` +
      `<circle cx="11" cy="11" r="4" fill="white"/>` +
      `<path d="M20.0001 11.0001C19.9999 11.0001 19.9998 10.9999 19.9998 10.9998C19.9372 8.45798 19.0567 6.33843 17.3587 4.64124C15.6608 2.94399 13.5411 2.06359 10.9999 2.00016C8.45803 2.06289 6.33839 2.94325 4.64115 4.64124C2.94391 6.33923 2.06352 8.45888 2.00009 11.0001C2.00009 12.8541 2.74471 14.9737 4.23401 17.3589C4.97692 18.5487 5.9103 19.802 7.03425 21.1188C9.12153 23.5642 12.8786 23.5639 14.9659 21.1185C16.0897 19.8018 17.0232 18.5487 17.7664 17.3589C19.2563 14.9738 20.0009 12.8542 20.0004 11.0004C20.0004 11.0002 20.0002 11.0001 20.0001 11.0001V11.0001ZM11 28C10.9999 28 10.9999 28 10.9998 27.9999C3.66679 21.3337 0 15.6669 0 11.0001C0.0832563 7.87465 1.15622 5.28124 3.21894 3.21896C5.28166 1.15691 7.87535 0.0839425 11 0C14.1254 0.0832568 16.7188 1.15622 18.7811 3.21896C20.8431 5.28169 21.9161 7.8754 22 11.0001C22 15.6668 18.3336 21.3337 11.0002 27.9999C11.0001 28 11.0001 28 11 28V28ZM10.9999 14.0001C11.8539 13.9793 12.5622 13.6878 13.1249 13.1251C13.6876 12.5624 13.9689 11.8541 13.9689 11.0001C13.9689 10.146 13.6876 9.43779 13.1249 8.87505C12.5621 8.31231 11.8539 8.03105 10.9999 8.03105C10.1458 8.03105 9.43762 8.3123 8.87488 8.87505C8.31214 9.4378 8.03089 10.146 8.03089 11.0001C8.03089 11.8541 8.31214 12.5623 8.87488 13.1251C9.43762 13.6878 10.1458 13.9793 10.9999 14.0001ZM10.9999 16C9.58316 15.9581 8.40614 15.4684 7.46889 14.5311C6.53165 13.5938 6.04188 12.4168 5.99994 11.0001C6.0419 9.58333 6.53163 8.40631 7.46889 7.46906C8.40614 6.5318 9.58316 6.04203 10.9999 6.00009C12.4166 6.04205 13.5936 6.53178 14.5309 7.46906C15.4681 8.40631 15.9579 9.58333 15.9998 11.0001C15.9579 12.4168 15.4681 13.5938 14.5309 14.5311C13.5936 15.4683 12.4166 15.9581 10.9999 16Z" fill="${pinColors.borderColor}"/></svg>`;

    return encodeSvg(svg);
  }, [encodeSvg, pinColors]);

  const svgMarkerSelected: string = useMemo(() => {
    const svg =
      `<svg width="22" height="28" viewBox="0 0 22 28" fill="none" xmlns="http://www.w3.org/2000/svg">` +
      `<path d="M5.00016 20.5L10.0002 25.5C11.8334 24.3333 15.5 21.9 15.5 21.5C15.5 21.1 18.5 17.3333 20 15.5C20.3333 14.1667 21 11.4 21 11C21 10.6 20.3333 8.16667 20 7L16.5 2.5L11 0.5L5.00016 2.5L1.50016 7C1.33349 8.83333 1.10016 12.7 1.50016 13.5L5.00016 20.5Z" fill="${pinColorsSelected.fillColor}"/>` +
      `<circle cx="11" cy="11" r="4" fill="white"/>` +
      `<path d="M20.0001 11.0001C19.9999 11.0001 19.9998 10.9999 19.9998 10.9998C19.9372 8.45798 19.0567 6.33843 17.3587 4.64124C15.6608 2.94399 13.5411 2.06359 10.9999 2.00016C8.45803 2.06289 6.33839 2.94325 4.64115 4.64124C2.94391 6.33923 2.06352 8.45888 2.00009 11.0001C2.00009 12.8541 2.74471 14.9737 4.23401 17.3589C4.97692 18.5487 5.9103 19.802 7.03425 21.1188C9.12153 23.5642 12.8786 23.5639 14.9659 21.1185C16.0897 19.8018 17.0232 18.5487 17.7664 17.3589C19.2563 14.9738 20.0009 12.8542 20.0004 11.0004C20.0004 11.0002 20.0002 11.0001 20.0001 11.0001V11.0001ZM11 28C10.9999 28 10.9999 28 10.9998 27.9999C3.66679 21.3337 0 15.6669 0 11.0001C0.0832563 7.87465 1.15622 5.28124 3.21894 3.21896C5.28166 1.15691 7.87535 0.0839425 11 0C14.1254 0.0832568 16.7188 1.15622 18.7811 3.21896C20.8431 5.28169 21.9161 7.8754 22 11.0001C22 15.6668 18.3336 21.3337 11.0002 27.9999C11.0001 28 11.0001 28 11 28V28ZM10.9999 14.0001C11.8539 13.9793 12.5622 13.6878 13.1249 13.1251C13.6876 12.5624 13.9689 11.8541 13.9689 11.0001C13.9689 10.146 13.6876 9.43779 13.1249 8.87505C12.5621 8.31231 11.8539 8.03105 10.9999 8.03105C10.1458 8.03105 9.43762 8.3123 8.87488 8.87505C8.31214 9.4378 8.03089 10.146 8.03089 11.0001C8.03089 11.8541 8.31214 12.5623 8.87488 13.1251C9.43762 13.6878 10.1458 13.9793 10.9999 14.0001ZM10.9999 16C9.58316 15.9581 8.40614 15.4684 7.46889 14.5311C6.53165 13.5938 6.04188 12.4168 5.99994 11.0001C6.0419 9.58333 6.53163 8.40631 7.46889 7.46906C8.40614 6.5318 9.58316 6.04203 10.9999 6.00009C12.4166 6.04205 13.5936 6.53178 14.5309 7.46906C15.4681 8.40631 15.9579 9.58333 15.9998 11.0001C15.9579 12.4168 15.4681 13.5938 14.5309 14.5311C13.5936 15.4683 12.4166 15.9581 10.9999 16Z" fill="${pinColorsSelected.borderColor}"/></svg>`;

    return encodeSvg(svg);
  }, [encodeSvg, pinColorsSelected]);

  const renderer = useMemo(
    () => ({
      render: ({ count, position }) =>
        new google.maps.Marker({
          label: { text: String(count), color: '#000' },
          position,
          icon: {
            url: svgCluster,
            scaledSize: new google.maps.Size(60, 60),
          },
        }),
    }),
    [pinColors, svgCluster],
  );

  const markerOptions = useMemo(
    () => ({
      map,
      options: {
        optimized: false,
        icon: {
          url: svgMarker,
        },
      } as google.maps.MarkerOptions,
    }),
    [map, svgMarker],
  );

  const markerSelectedOptions = useMemo(
    () => ({
      map,
      options: {
        optimized: false,
        icon: {
          url: svgMarkerSelected,
        },
      } as google.maps.MarkerOptions,
    }),
    [map, svgMarkerSelected],
  );

  useEffect(() => {
    if (markers.length > 0) {
      markers.forEach(marker => {
        // @ts-ignore
        if (marker.id === activeId) {
          marker.setOptions(markerSelectedOptions);
        } else {
          marker.setOptions(markerOptions);
        }

        if (onSelect) {
          google.maps.event.addListener(marker, 'click', () =>
            onSelect(marker.get('id')),
          );
        }
      });
    }

    return () => {
      if (markers.length > 0) {
        markers.forEach(marker => marker.setMap(null));
      }
    };
  }, [markers, markerOptions, activeId]);

  useEffect(() => {
    function setupClusters() {
      new MarkerClusterer({ map, markers, renderer });
    }

    setupClusters();

    () => setupClusters();
  }, [map, markers, renderer]);

  return null;
};

const pinColors = {
  primary: {
    fillColor: green[400],
    borderColor: green[500],
  },
  secondary: {
    fillColor: lilac[300],
    borderColor: lilac[600],
  },
};

const LocationsMap: React.FC<LocationsMapProps> = memo(
  ({
    className = '',
    locations,
    center,
    variant = 'primary',
    activeId,
    onSelect = null,
    zoom = 13,
  }) => {
    const mapOptions: MapProps['mapOptions'] = {
      center,
      zoom,
      disableDefaultUI: true,
      scrollwheel: true,
      zoomControl: false,
      styles: [
        {
          featureType: 'all',
          elementType: 'all',
          stylers: [
            {
              saturation: '-100',
            },
            {
              visibility: 'simplified',
            },
            {
              lightness: '10',
            },
          ],
        },
        {
          featureType: 'all',
          elementType: 'labels',
          stylers: [
            {
              visibility: 'simplified',
            },
          ],
        },
      ],
    };

    return (
      <Wrapper apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}>
        <Map
          {...{
            className,
            mapOptions,
            locations,
            onSelect,
          }}
        >
          <Markers {...{ activeId, pinColors: pinColors[variant], onSelect }} />
        </Map>
      </Wrapper>
    );
  },
);

export default LocationsMap;
